/**
 * PATH: packages/quote-builder/src/grid/formulaRepair.ts
 * FILE_ID: QB-GRID-FORMULAREPAIR-001
 * PURPOSE: Excel-style "commit forgiveness" for A1 formulas — pure text
 *   repair (auto-close parens, strip dangling operators), canonical
 *   commit form (uppercase functions + cell refs), and human-readable
 *   error messages for the codes the engine raises.
 * WHY: Operator 2026-07-06 — "=suM(A2" showed a bare #ERROR!. Excel
 *   repairs recoverable typos on commit; our parser hard-failed and the
 *   reason was discarded. This module is the forgiveness + explanation
 *   layer for EVERY consumer of the shared grid (kanban calculator,
 *   compose workbook, quote builder surfaces).
 * FLOW:
 *   raw body → strip dangling trailing ops → balance "(" → parseable body
 *   raw "=…" → repaired + uppercased fn/cell refs → canonical commit text
 *   engine message → humanizeA1FormulaError → tooltip / formula-bar text
 * DEPENDENCIES: none (pure string functions — unit-testable in isolation)
 */

/** Trailing characters that mean "the formula is unfinished, not wrong". */
const DANGLING_TAIL_RE = /[+\-*/^,:<>=&\s]+$/

/** Matches a complete Excel string literal ("" is the escaped quote). */
const STRING_LITERAL_RE = /"(?:[^"]|"")*"/g

/**
 * Pure: apply `fn` to the segments of `text` OUTSIDE complete string
 * literals, leaving the literals byte-for-byte intact. The workhorse for
 * every A1 text transform (repair, $-stripping, canonical casing) so
 * quoted content is never rewritten.
 */
export function transformOutsideStrings(
  text: string,
  fn: (segment: string) => string,
): string {
  let out = ''
  let last = 0
  for (const m of text.matchAll(STRING_LITERAL_RE)) {
    out += fn(text.slice(last, m.index)) + m[0]
    last = (m.index ?? 0) + m[0].length
  }
  out += fn(text.slice(last))
  return out
}

/**
 * Pure: repair a formula BODY (text after "="), returning a best-effort
 * parseable expression. Never throws; returns '' when nothing usable
 * remains (caller decides how to treat an empty body).
 *
 *  - strips dangling trailing binary operators / commas / range colons
 *  - auto-closes unbalanced "(" (Excel commit parity)
 *  - leaves over-closed ")" untouched (not safely repairable)
 */
/** Masked view: string literals become NUL runs of equal length, so
 *  structural regex/paren decisions never see quoted content. */
export function maskA1StringLiterals(text: string): string {
  return text.replace(STRING_LITERAL_RE, (m) => '\u0000'.repeat(m.length))
}

export function repairA1FormulaBody(body: string): string {
  let text = body.trim()
  if (text === '') return ''

  // $A$1-style absolute refs evaluate identically to A1 — strip the "$"
  // (outside string literals) so Excel-pasted formulas just work.
  text = transformOutsideStrings(text, (seg) => seg.replace(/\$(?=[A-Za-z0-9])/g, ''))

  // An unterminated string literal is unfinished typing — auto-close it
  // ("" escapes are quote PAIRS, so plain parity is correct).
  const quoteCount = (text.match(/"/g) ?? []).length
  if (quoteCount % 2 === 1) text += '"'

  // Strip dangling tail chars (e.g. "1+", "SUM(A1:", "A1,") — decided on
  // the masked view so a tail inside a string literal is never touched.
  let mask = maskA1StringLiterals(text)
  const tail = mask.match(DANGLING_TAIL_RE)
  if (tail && tail.index !== undefined) {
    text = text.slice(0, tail.index)
    mask = mask.slice(0, tail.index)
  }
  // A body that is NOTHING BUT dangling operators/parens is in progress.
  if (text === '' || /^[()\s]*$/.test(mask)) return ''

  // Balance parentheses, counting on the masked view.
  let open = 0
  for (const c of mask) {
    if (c === '(') open++
    else if (c === ')' && open > 0) open--
  }
  if (open > 0) text += ')'.repeat(open)

  return text
}

const FN_CALL_RE = /([A-Za-z_][A-Za-z0-9_.]*)(\s*\()/g
const CELL_REF_RE = /\b([a-z]{1,3})(\d{1,4})\b/g

/**
 * Pure: canonical commit form of a raw cell text (Excel parity —
 * Excel rewrites "=sum(a2" to "=SUM(A2)" when you press Enter).
 * Non-formula text is returned unchanged. The repair is conservative:
 * if the repaired body is empty the original text is returned so the
 * "formula in progress" handling upstream still applies.
 */
export function canonicalizeA1FormulaText(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed.startsWith('=')) return raw
  const body = repairA1FormulaBody(trimmed.slice(1))
  if (body === '') return raw
  const cased = transformOutsideStrings(body, (seg) =>
    seg
      .replace(FN_CALL_RE, (_m, name: string, paren: string) => name.toUpperCase() + paren)
      .replace(CELL_REF_RE, (m: string, letters: string, digits: string, offset: number, whole: string) => {
        // Don't uppercase an identifier that is a function call ("if(" stays handled above).
        const after = whole.slice(offset + m.length).trimStart()
        if (after.startsWith('(')) return m
        return letters.toUpperCase() + digits
      }),
  )
  return `=${cased}`
}

const ERROR_HUMANIZERS: Array<[RegExp, string | ((m: RegExpMatchArray) => string)]> = [
  [/expected RPAREN/i, 'Missing closing bracket ")"'],
  [/unterminated string/i, 'Missing closing quote (")'],
  [/needs a number, got text '(.+)'/i, (m) => `"${m[1]}" is text — this operation needs a number`],
  [/unexpected trailing tokens/i, 'Unexpected text after the end of the formula'],
  [/division by zero/i, 'Division by zero'],
  [/unknown function '(.+)'/i, (m) => `Unknown function "${m[1].toUpperCase()}"`],
  [/unknown reference '(.+)'/i, (m) => `"${m[1].toUpperCase()}" is text or not a cell — arithmetic needs numbers`],
  [/unexpected char '(.+)' at (\d+)/i, (m) => `Character "${m[1]}" is not supported here`],
  [/unexpected token at (\d+)/i, 'Incomplete expression'],
  [/bad number/i, 'Malformed number'],
  [/empty expression/i, 'Formula is empty'],
]

/** Pure: engine/parser message → short human explanation for tooltips. */
export function humanizeA1FormulaError(message: string): string {
  for (const [re, out] of ERROR_HUMANIZERS) {
    const m = message.match(re)
    if (m) return typeof out === 'function' ? out(m) : out
  }
  return message || 'Formula could not be evaluated'
}
